import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Ticket, Plus, Search, RefreshCw, Check, X, Slash, Lock, Trash2, AlertTriangle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCT = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        }) + ' CT';
    } catch { return ''; }
};

/**
 * Admin panel for Lucky 5 — tiered sports lottery.
 *
 * Flow:
 *   1. Pick a sport from the dropdown → loads events
 *   2. Tap an event → adds an outcome (h2h moneyline by default) to the
 *      "selected picks" panel on the right. Reach 5 picks to enable Create.
 *   3. Adjust entry price + tier payouts ($5 / $25 / $100 defaults)
 *   4. Click "Launch Lucky 5" — auto-cancels any previous pack and broadcasts
 *      notification to every active player.
 *   5. Below: list of packs. For the active one, grade each pick (win/loss/void)
 *      then settle. Cancel before settlement to refund all entries.
 */
export const LuckyPackAdminPanel = () => {
    const [sports, setSports] = useState([]);
    const [sportKey, setSportKey] = useState('');
    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [eventQuery, setEventQuery] = useState('');

    const [selectedPicks, setSelectedPicks] = useState([]);  // array of picks (max 5)
    const [name, setName] = useState('Lucky 5');
    const [entryPrice, setEntryPrice] = useState(5);
    const [payout3, setPayout3] = useState(5);
    const [payout4, setPayout4] = useState(25);
    const [payout5, setPayout5] = useState(100);
    const [submitting, setSubmitting] = useState(false);

    const [packs, setPacks] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [gradingPackId, setGradingPackId] = useState(null);

    // ── Load sports + history once
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/sports`);
                const all = Object.values(data.groups || {}).flat().filter(s => !s.has_outrights);
                setSports(all);
                const preferred = all.find(s => ['americanfootball_nfl', 'basketball_nba', 'icehockey_nhl', 'baseball_mlb'].includes(s.key));
                if (preferred) setSportKey(preferred.key);
                else if (all[0]) setSportKey(all[0].key);
            } catch { toast.error('Could not load sports list'); }
        })();
    }, []);

    useEffect(() => {
        if (!sportKey) return;
        setEventsLoading(true);
        (async () => {
            try {
                const { data } = await axios.get(`${API}/sports/${sportKey}/odds`);
                setEvents(data.events || []);
            } catch {
                setEvents([]);
                toast.error('Could not load events for that sport');
            }
            setEventsLoading(false);
        })();
    }, [sportKey]);

    const loadHistory = useCallback(async () => {
        setListLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/lucky-packs?limit=25`);
            setPacks(data.rows || []);
            const active = (data.rows || []).find(p => p.status === 'active');
            if (active && !gradingPackId) setGradingPackId(active.id);
        } catch { /* silent */ }
        setListLoading(false);
    }, [gradingPackId]);

    useEffect(() => { loadHistory(); }, [loadHistory]);

    const addPick = (event, market = 'h2h', outcomeIdx = 0) => {
        if (selectedPicks.length >= 5) {
            toast.error('Already have 5 picks — remove one first.');
            return;
        }
        if (selectedPicks.some(p => p.event_id === event.id)) {
            toast.error('That event is already in the pack.');
            return;
        }
        const m = event.markets?.[market];
        const outcome = m?.outcomes?.[outcomeIdx];
        if (!outcome) { toast.error('That market is not available for this event.'); return; }
        setSelectedPicks(prev => [...prev, {
            event_id: event.id,
            sport_key: event.sport_key || sportKey,
            sport_title: sports.find(s => s.key === sportKey)?.title || '',
            market,
            outcome_name: outcome.name,
            point: outcome.point ?? null,
            odds: outcome.price,
            home_team: event.home_team,
            away_team: event.away_team,
            commence_time: event.commence_time,
        }]);
    };

    const removePick = (idx) => setSelectedPicks(prev => prev.filter((_, i) => i !== idx));

    const create = async () => {
        if (selectedPicks.length !== 5) { toast.error('Pick exactly 5 events first.'); return; }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/admin/lucky-pack`, {
                name: name.trim() || 'Lucky 5',
                entry_price: Number(entryPrice),
                payouts: { '3': Number(payout3), '4': Number(payout4), '5': Number(payout5) },
                picks: selectedPicks,
                notify: true,
            });
            toast.success(`Lucky 5 live! Notified ${data.notified_count} players.`);
            setSelectedPicks([]);
            await loadHistory();
            setGradingPackId(data.pack.id);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not create Lucky 5 pack');
        }
        setSubmitting(false);
    };

    const gradePick = async (packId, pickIdx, result) => {
        try {
            await axios.post(`${API}/admin/lucky-pack/${packId}/grade-pick`, { pick_index: pickIdx, result });
            toast.success(`Pick #${pickIdx + 1} → ${result.toUpperCase()}`);
            await loadHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not grade pick');
        }
    };

    const settle = async (packId) => {
        if (!window.confirm('Settle the pack now? This pays out winners and is irreversible.')) return;
        try {
            const { data } = await axios.post(`${API}/admin/lucky-pack/${packId}/settle`);
            toast.success(`Settled — ${data.winners_count} winners, $${Number(data.total_payouts).toFixed(2)} paid.`);
            await loadHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not settle');
        }
    };

    const cancel = async (packId) => {
        if (!window.confirm('Cancel the pack and refund all entries?')) return;
        try {
            const { data } = await axios.post(`${API}/admin/lucky-pack/${packId}/cancel`);
            toast.success(`Cancelled — refunded ${data.refunded} entries.`);
            await loadHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not cancel');
        }
    };

    const filteredEvents = events.filter(e => {
        if (!eventQuery.trim()) return true;
        const q = eventQuery.toLowerCase();
        return e.home_team.toLowerCase().includes(q) || e.away_team.toLowerCase().includes(q);
    });

    const activePack = packs.find(p => p.status === 'active');
    const gradingPack = packs.find(p => p.id === gradingPackId) || activePack;

    return (
        <div className="space-y-5" data-testid="lucky-pack-admin">
            {/* ── BUILDER ── */}
            <div className="card-dark p-5" style={{ borderLeft: '3px solid #FFD700' }}>
                <div className="flex items-center gap-2 mb-1">
                    <Ticket className="w-5 h-5 text-[#FFD700]" />
                    <h3 className="text-lg font-bold text-white">Lucky 5 — Tiered Sports Lottery</h3>
                </div>
                <p className="text-xs text-[#A0AAB2] mb-4">
                    Build a 5-pick lottery pack. Players pay one fee and win tiered payouts based on hits.
                    Launching a new one auto-replaces any active pack.
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
                    {/* Left: event browser */}
                    <div>
                        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-3 mb-3">
                            <div>
                                <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Sport</Label>
                                <select
                                    value={sportKey}
                                    onChange={(e) => setSportKey(e.target.value)}
                                    className="w-full h-9 rounded-md bg-[#0a0a0a] border border-white/10 text-white text-sm px-3"
                                    data-testid="lp-sport-select"
                                >
                                    {sports.map(s => (
                                        <option key={s.key} value={s.key}>{s.title}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Filter events</Label>
                                <div className="relative">
                                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A0AAB2]" />
                                    <Input
                                        value={eventQuery}
                                        onChange={(e) => setEventQuery(e.target.value)}
                                        placeholder="Filter by team name…"
                                        className="pl-8 h-9 bg-[#0a0a0a] border-white/10 text-white text-sm"
                                        data-testid="lp-event-filter"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="rounded-lg border border-white/10 bg-[#0a0a0a] max-h-[420px] overflow-y-auto">
                            {eventsLoading ? (
                                <div className="text-center text-[11px] text-[#A0AAB2] py-6">Loading events…</div>
                            ) : filteredEvents.length === 0 ? (
                                <div className="text-center text-[11px] text-[#A0AAB2] py-6">
                                    No events {eventQuery ? 'match that filter' : 'available'}.
                                </div>
                            ) : filteredEvents.slice(0, 50).map(e => {
                                const already = selectedPicks.some(p => p.event_id === e.id);
                                const markets = e.markets || {};
                                return (
                                    <div
                                        key={e.id}
                                        className={`px-3 py-2 text-xs border-b border-white/5 last:border-0 ${already ? 'opacity-50' : ''}`}
                                        data-testid={`lp-event-${e.id}`}
                                    >
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                            <span className="font-semibold text-white truncate">{e.away_team} @ {e.home_team}</span>
                                            <span className="text-[10px] text-[#666] whitespace-nowrap">{fmtCT(e.commence_time)}</span>
                                        </div>
                                        <div className="flex flex-wrap gap-1">
                                            {(markets.h2h?.outcomes || []).map((o, i) => (
                                                <button
                                                    key={`h2h-${i}`}
                                                    type="button"
                                                    disabled={already}
                                                    onClick={() => addPick(e, 'h2h', i)}
                                                    className="text-[10px] px-2 py-1 rounded bg-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/20 disabled:cursor-not-allowed"
                                                    data-testid={`lp-add-h2h-${e.id}-${i}`}
                                                >
                                                    {o.name} ({o.price > 0 ? '+' : ''}{o.price})
                                                </button>
                                            ))}
                                            {(markets.spreads?.outcomes || []).slice(0, 2).map((o, i) => (
                                                <button
                                                    key={`sp-${i}`}
                                                    type="button"
                                                    disabled={already}
                                                    onClick={() => addPick(e, 'spreads', i)}
                                                    className="text-[10px] px-2 py-1 rounded bg-[#39FF14]/10 border border-[#39FF14]/30 text-[#39FF14] hover:bg-[#39FF14]/20 disabled:cursor-not-allowed"
                                                    data-testid={`lp-add-sp-${e.id}-${i}`}
                                                >
                                                    {o.name} {o.point > 0 ? '+' : ''}{o.point}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Right: selected picks */}
                    <div className="rounded-lg border border-[#FFD700]/30 bg-[#FFD700]/[0.04] p-3 flex flex-col">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-[11px] uppercase tracking-wider font-black text-[#FFD700]">
                                Selected picks ({selectedPicks.length}/5)
                            </p>
                            {selectedPicks.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setSelectedPicks([])}
                                    className="text-[10px] text-[#A0AAB2] hover:text-[#FF3B30]"
                                    data-testid="lp-clear-picks"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        {selectedPicks.length === 0 ? (
                            <p className="text-center text-[11px] text-[#A0AAB2] py-4">
                                Tap a team/spread chip on the left to add picks.
                            </p>
                        ) : (
                            <ul className="space-y-1.5 mb-3">
                                {selectedPicks.map((p, i) => (
                                    <li
                                        key={`${p.event_id}-${i}`}
                                        className="flex items-center gap-2 text-xs bg-[#0a0a0a] rounded p-2"
                                        data-testid={`lp-picked-${i}`}
                                    >
                                        <span className="text-[#FFD700] font-bold">{i + 1}.</span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white font-bold truncate">
                                                {p.outcome_name}
                                                {p.point != null && ` ${p.point > 0 ? '+' : ''}${p.point}`}
                                            </p>
                                            <p className="text-[10px] text-[#A0AAB2] truncate">
                                                {p.away_team} @ {p.home_team}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removePick(i)}
                                            className="text-[#A0AAB2] hover:text-[#FF3B30] shrink-0"
                                            data-testid={`lp-remove-${i}`}
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Config */}
                        <div className="space-y-2 mt-auto">
                            <div>
                                <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Pack name</Label>
                                <Input
                                    value={name}
                                    onChange={(e) => setName(e.target.value.slice(0, 60))}
                                    className="h-8 bg-[#0a0a0a] border-white/10 text-white text-sm"
                                    data-testid="lp-name-input"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Entry $</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={entryPrice}
                                        onChange={(e) => setEntryPrice(e.target.value)}
                                        className="h-8 bg-[#0a0a0a] border-white/10 text-white text-sm"
                                        data-testid="lp-entry-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-[10px] text-[#A0AAB2] mb-1 block">3-hit $</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={payout3}
                                        onChange={(e) => setPayout3(e.target.value)}
                                        className="h-8 bg-[#0a0a0a] border-white/10 text-white text-sm"
                                        data-testid="lp-p3-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-[10px] text-[#A0AAB2] mb-1 block">4-hit $</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={payout4}
                                        onChange={(e) => setPayout4(e.target.value)}
                                        className="h-8 bg-[#0a0a0a] border-white/10 text-white text-sm"
                                        data-testid="lp-p4-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-[10px] text-[#A0AAB2] mb-1 block">5-hit $</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={payout5}
                                        onChange={(e) => setPayout5(e.target.value)}
                                        className="h-8 bg-[#0a0a0a] border-white/10 text-white text-sm"
                                        data-testid="lp-p5-input"
                                    />
                                </div>
                            </div>

                            <Button
                                onClick={create}
                                disabled={submitting || selectedPicks.length !== 5}
                                className="w-full font-black mt-2"
                                style={{ background: 'linear-gradient(135deg, #FFD700, #FF007F)', color: 'black' }}
                                data-testid="lp-launch-btn"
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                {submitting ? 'Launching…' : `Launch Lucky 5`}
                            </Button>
                            {activePack && (
                                <p className="text-[10px] text-[#FF8C00] flex items-start gap-1 mt-1">
                                    <AlertTriangle className="w-3 h-3 shrink-0 mt-px" />
                                    Launching will auto-replace the active pack.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── GRADING PANEL ── */}
            {gradingPack && (
                <div className="card-dark p-5" style={{ borderLeft: '3px solid #39FF14' }}>
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div>
                            <h4 className="text-base font-bold text-white flex items-center gap-2">
                                <Lock className="w-4 h-4 text-[#39FF14]" />
                                Grade & Settle: {gradingPack.name}
                            </h4>
                            <p className="text-[11px] text-[#A0AAB2]">
                                {gradingPack.total_entries} entries · status: <span className="font-bold text-white">{gradingPack.status}</span>
                            </p>
                        </div>
                        {packs.length > 1 && (
                            <select
                                value={gradingPackId || ''}
                                onChange={(e) => setGradingPackId(e.target.value)}
                                className="h-8 rounded bg-[#0a0a0a] border border-white/10 text-white text-xs px-2"
                                data-testid="lp-pack-switcher"
                            >
                                {packs.map(p => (
                                    <option key={p.id} value={p.id}>{p.name} — {p.status} ({fmtCT(p.created_at)})</option>
                                ))}
                            </select>
                        )}
                    </div>

                    <ul className="space-y-2">
                        {(gradingPack.picks || []).map((p, i) => {
                            const result = p.result;
                            const settled = gradingPack.status === 'settled' || gradingPack.status === 'cancelled';
                            return (
                                <li
                                    key={i}
                                    className="rounded-lg bg-[#0a0a0a] border border-white/10 p-3"
                                    data-testid={`lp-grade-row-${i}`}
                                >
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white font-bold text-sm">
                                                <span className="text-[#FFD700] mr-1">{i + 1}.</span>
                                                {p.outcome_name}
                                                {p.point != null && ` ${p.point > 0 ? '+' : ''}${p.point}`}
                                                <span className="text-[#A0AAB2] font-normal ml-2">
                                                    ({p.away_team} @ {p.home_team})
                                                </span>
                                            </p>
                                            <p className="text-[10px] text-[#A0AAB2] mt-0.5">
                                                Kickoff {fmtCT(p.commence_time)} · {p.market}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            {result && (
                                                <span className={`text-[10px] font-black uppercase px-2 py-1 rounded ${result === 'win' ? 'bg-[#39FF14]/20 text-[#39FF14]' : result === 'loss' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' : 'bg-[#FFD700]/20 text-[#FFD700]'}`}>
                                                    {result}
                                                </span>
                                            )}
                                            {!settled && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => gradePick(gradingPack.id, i, 'win')}
                                                        className={`h-7 px-2 rounded text-[10px] font-bold border ${result === 'win' ? 'bg-[#39FF14] text-black border-[#39FF14]' : 'border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10'}`}
                                                        data-testid={`lp-grade-win-${i}`}
                                                    >
                                                        <Check className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => gradePick(gradingPack.id, i, 'loss')}
                                                        className={`h-7 px-2 rounded text-[10px] font-bold border ${result === 'loss' ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10'}`}
                                                        data-testid={`lp-grade-loss-${i}`}
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => gradePick(gradingPack.id, i, 'void')}
                                                        className={`h-7 px-2 rounded text-[10px] font-bold border ${result === 'void' ? 'bg-[#FFD700] text-black border-[#FFD700]' : 'border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10'}`}
                                                        data-testid={`lp-grade-void-${i}`}
                                                    >
                                                        <Slash className="w-3 h-3" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>

                    {gradingPack.status === 'active' && (
                        <div className="flex gap-2 mt-4 flex-wrap">
                            <Button
                                onClick={() => settle(gradingPack.id)}
                                className="font-bold"
                                style={{ background: 'linear-gradient(135deg, #39FF14, #008f0b)', color: 'black' }}
                                data-testid="lp-settle-btn"
                            >
                                <Lock className="w-4 h-4 mr-1" /> Settle & Pay Winners
                            </Button>
                            <Button
                                onClick={() => cancel(gradingPack.id)}
                                variant="outline"
                                className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                                data-testid="lp-cancel-btn"
                            >
                                <Trash2 className="w-4 h-4 mr-1" /> Cancel & Refund All
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* ── HISTORY ── */}
            <div className="card-dark p-4">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-white">All Lucky 5 Packs</h4>
                    <button
                        type="button"
                        onClick={loadHistory}
                        className="text-[#A0AAB2] hover:text-white"
                        title="Refresh"
                        data-testid="lp-refresh"
                    >
                        <RefreshCw className={`w-4 h-4 ${listLoading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
                {packs.length === 0 ? (
                    <p className="text-center text-[11px] text-[#A0AAB2] py-4">No packs yet.</p>
                ) : (
                    <ul className="space-y-1.5">
                        {packs.map(p => (
                            <li
                                key={p.id}
                                className={`rounded-lg p-2.5 border ${p.status === 'active' ? 'bg-[#FFD700]/10 border-[#FFD700]/40' : 'bg-[#0a0a0a] border-white/10'}`}
                                data-testid={`lp-history-${p.id}`}
                            >
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${p.status === 'active' ? 'bg-[#39FF14] text-black' : p.status === 'settled' ? 'bg-[#A0AAB2]/30 text-[#A0AAB2]' : 'bg-[#FF3B30]/30 text-[#FF3B30]'}`}>
                                        {p.status.toUpperCase()}
                                    </span>
                                    <span className="text-white text-sm font-bold flex-1 truncate">{p.name}</span>
                                    <span className="text-[10px] text-[#A0AAB2]">
                                        {p.total_entries} entries · ${Number(p.total_payouts).toFixed(0)} paid
                                    </span>
                                    {p.status !== 'active' && (
                                        <button
                                            type="button"
                                            onClick={() => setGradingPackId(p.id)}
                                            className="text-[10px] text-[#FFD700] hover:underline"
                                            data-testid={`lp-view-${p.id}`}
                                        >
                                            View
                                        </button>
                                    )}
                                </div>
                                <p className="text-[10px] text-[#A0AAB2] mt-0.5">
                                    Created {fmtCT(p.created_at)} · ${Number(p.entry_price).toFixed(0)} in →
                                    ${Number(p.payouts?.['3'] || 0).toFixed(0)} / ${Number(p.payouts?.['4'] || 0).toFixed(0)} / ${Number(p.payouts?.['5'] || 0).toFixed(0)}
                                </p>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default LuckyPackAdminPanel;
